import { TFunction } from "i18next";
import * as yup from "yup";
import { InferType } from "yup";

interface Role {
  _id: string;
  type: string;
}

const inviteValidationSchema = (
  t: TFunction,
  country: string,
  phoneNumberRequired: string,
  isEditMode: boolean = false,
  isProgramRequired: boolean = false,
  roles: Role[] = []
) => {
  return yup.lazy(() => {
    const localeValidation = (): yup.StringSchema => {
      const base = (() => {
        switch (country) {
          case "US":
            return yup
              .string()
              .test(
                "min",
                t("validations.validator_digits_min", { count: 10 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length >= 10;
                }
              )
              .test(
                "max",
                t("validations.validator_digits_max", { count: 10 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length <= 10;
                }
              )
              .test("phoneNumber", t("validations.invalid_format"), (value) => {
                if (!value || value.trim() === "") return true;
                const phoneRegex = /^\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/;
                return phoneRegex.test(value);
              });
          case "UK":
            return yup
              .string()
              .test(
                "min",
                t("validations.validator_digits_min", { count: 10 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length >= 10;
                }
              )
              .test(
                "max",
                t("validations.validator_digits_max", { count: 10 }),
                (value) => {
                  if (!value || value.length === 0) return true;
                  return value.length <= 10;
                }
              )
              .test("phoneNumber", t("validations.invalid_format"), (value) => {
                if (!value || value.trim() === "") return true;
                const phoneRegex = /^\(?\d{4}\)?[-. ]?\d{6}$/;
                return phoneRegex.test(value);
              });
          default:
            return yup.string();
        }
      })();
      return phoneNumberRequired === "mandatory"
        ? base.required(t("validations.required"))
        : base.optional();
    };
    
    const schemaFields: yup.ObjectShape = {
      firstName: yup
        .string()
        .required("This field is required")
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      lastName: yup
        .string()
        .required("This field is required")
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      email: yup
        .string()
        .required("This field is required")
        .email(t("validations.invalid_format"))
        .max(100, t("validations.max_length", { count: 100 })),
      phoneNumber: localeValidation(),
      externalId: yup
        .string()
        .optional()
        .test("max", t("validations.max_length", { count: 30 }), (value) => {
          if (!value || value.length === 0) return true;
          return value.length <= 30;
        }),
      roles: yup.string().required("This field is required"),

      // Nueva estructura para múltiples programas
      programs: yup
        .array()
        .of(
          yup.object().shape({
            programId: yup.string().required("Program is required"),
            orgUnitIds: yup
              .array()
              .of(yup.string().required())
              .min(1, "At least one organization unit must be selected"),
          })
        )
        .test(
          "programs-required",
          "At least one program with organization units must be selected",
          function (value) {
            const { roles: selectedRole } = this.parent;

            // Check if the selected role is an admin role (STANDARD_SYSTEM_ROLE)
            const isAdminRole = roles.some(
              (role) =>
                role._id === selectedRole &&
                role.type === "STANDARD_SYSTEM_ROLE"
            );

            // If it's an admin role, programs are not required
            if (isAdminRole) {
              return true;
            }

            if (isProgramRequired) {
              return value && value.length > 0;
            }
            return true;
          }
        ),
    };

    if (isEditMode) {
      schemaFields["status"] = yup
        .string()
        .required("This field is required")
        .oneOf(["ACTIVE", "INACTIVE", "PENDING"], "Invalid status");
    }

    return yup.object().shape(schemaFields);
  });
};

export type InviteFormData = InferType<
  ReturnType<typeof inviteValidationSchema>
>;

export default inviteValidationSchema;
